import { useState } from 'react'
import { sendEmailVerification, signOut } from 'firebase/auth'
import { auth } from '../lib/firebase'
import { authErrorMessage } from '../lib/authErrors'
import { useAuth } from '../providers/auth/useAuth'
import AuthCard from '../components/auth/AuthCard'

const PRIMARY =
  'inline-flex h-11 w-full items-center justify-center rounded-xl bg-linear-to-b from-stone-700 to-stone-950 text-[15px] font-medium text-white transition-opacity enabled:hover:opacity-90 disabled:opacity-60'
const OUTLINE =
  'inline-flex h-11 w-full items-center justify-center rounded-xl border border-stone-300 bg-white text-[15px] font-medium text-stone-900 transition-colors enabled:hover:bg-stone-50 disabled:opacity-60'

export default function VerifyEmail() {
  const { user, refreshAccount } = useAuth()
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function resend() {
    if (!auth.currentUser) return
    setBusy(true)
    setError(null)
    try {
      await sendEmailVerification(auth.currentUser)
      setSent(true)
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  // Firebase caches the claim in the local user object, so the flag only moves
  // after a reload — following the link in another tab changes nothing here.
  async function recheck() {
    if (!auth.currentUser) return
    setBusy(true)
    setError(null)
    try {
      await auth.currentUser.reload()
      await auth.currentUser.getIdToken(true)
      await refreshAccount()
      if (!auth.currentUser.emailVerified) {
        setError('Still not verified. Open the link in the email, then try again.')
      }
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthCard
      title="Confirm your email"
      subtitle={`We sent a link to ${user?.email ?? 'your address'}. Open it to finish setting up.`}
      footer={
        <button type="button" onClick={() => void signOut(auth)} className="underline underline-offset-2">
          Sign out
        </button>
      }
    >
      {error && <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}
      {sent && !error && (
        <p className="mb-4 rounded-lg bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          Sent. It can take a minute to arrive — check spam too.
        </p>
      )}

      <div className="flex flex-col gap-2.5">
        <button type="button" onClick={() => void recheck()} disabled={busy} className={PRIMARY}>
          {busy ? 'Checking…' : "I've confirmed it"}
        </button>
        <button type="button" onClick={() => void resend()} disabled={busy} className={OUTLINE}>
          Send it again
        </button>
      </div>
    </AuthCard>
  )
}
